import React, { FC, useContext, useEffect, useState } from "react";
import { withRouter } from "react-router-dom";
import moment from "moment";

import InputBox from "../../../../../components/input-box/input-box";
import SelectBox from "../../../../../components/select-box/select-box";
import CalendarBox from "../../../../../components/calendar-box/calendar-box";
import DicotomicButton from "../../../../../components/dicotomic-button";
import NationalitySelect from "../../../../../components/nationality-select/nationality-select";
import DicotomicCheckbox from "../../../../../components/dicotomic-checkbox/dicotomic-checkbox";
import PhoneBox from "../../../../../components/phone-box/phone-box";
import MainButton from "../../../../../components/main-button";
import LeftInfoModal from "../../../../../components/left-info-modal";
import CheckboxWrapper from "../checkbox-wrapper/checkbox-wrapper";
import PetForm from "../pet-form/pet-form";
import AddressForm from "../address/address-form";
import { PersonalInformationContainer } from "../../../../hirings/new-hiring/health/forms/personal-information/personal-information-style";
import { images } from "../../../../../assets";

import { PERSONA_TYPE, SEND_TO, SUPPLEMENTS_MATCH_PARAMS } from "../../../../../constants/supplements";
import { DOC_NUMBER_MAX_LENGTH } from "../../../../../constants";
import { DynamicProductsContext } from "../../../../../context/constants";
import { SupplementsContext } from "../../../../../context/supplements/SupplementsContext";
import SupplementsServices from "../../../../../services/SupplementsServices";
import { deepGet, downloadBlob } from "../../../../../utils/utility";
import {
  loadPayTypes,
  getProductIdFromCollective,
} from "../../new-insured/required-documents/utils";
import { getCollective } from "../../utils";
import { filterStringProperties } from "../policy-data/utils";
import { genderOptions, getDocumentTypes } from "../../../../../utils/validation";
import { ProductPayType } from "../../../../../services/PayTypesServices.dto";
import { VariableDataProps } from "../personal-basic-data/personal-basic-data.types";
import { SendTo, Supplement, SupplementChild } from "../../../../../services/SupplementsServices.dto";

const VariableData: FC<VariableDataProps> = ({
  formData,
  step,
  emailError,
  setEmailError,
  match
}) => {
  const { values, handleChange, errors } = formData;
  const context = useContext(DynamicProductsContext);
  const allProducts = deepGet(context, 'allProducts', null);
  const [payType, setPayType] = useState<ProductPayType[]>([]);
  const [downloadError, setDownloadError] = useState<boolean>(false);
  const [downloadMsg, setDownloadMsg] = useState<string>('');
  const [originalSendTo, setOriginalSendTo] = useState<SendTo | null>(null);

  const supplementsContext = useContext(SupplementsContext);
  const getSupplementsChilds = () => {
    const { supplementId, supplements } = supplementsContext;
    const supplement = supplements.find((supplement: Supplement) => supplementId === supplement.id);
    const childs = supplement ? supplement.child : [];
    return childs;
  };
  const supplementChilds = getSupplementsChilds();

  useEffect(() => {
    if (allProducts && Object.keys(allProducts).length > 0) {
        loadPayTypes(getProductIdFromCollective(allProducts, getCollective()), setPayType);
    }
  }, [allProducts]);

  const handleChangeNationality = (label: string, code?: string) => {
    handleChange('newNationality', label);
    handleChange('newNationalityCode', code);
  };
    
  const handleOnCheck = (supplementChildId: number, name: string, value: boolean, step?: string) => {
    handleChange(name, value, step);
    if (name === "birthDate" && values.type !== PERSONA_TYPE.MASCOTA) {
      if (value) {
        if (!originalSendTo) {
          setOriginalSendTo(supplementsContext.sendTo);
        }
        supplementsContext.setSendTo(SEND_TO.BACKOFFICE);
      } else if (originalSendTo) {
        supplementsContext.setSendTo(originalSendTo);
      }
    }

    if (supplementChilds.length === 1 && supplementsContext.supplementChildIds.length === 1) return;
    // La elección de los suplementos condiciona el tipo de documentación requerida
    const childIndex = supplementsContext.supplementChildIds.indexOf(supplementChildId);
    if (childIndex !== -1) {
      const newChildIds = [...supplementsContext.supplementChildIds];
      newChildIds.splice(childIndex, 1);
      supplementsContext.setSupplementChildIds(newChildIds);
    } else {
      supplementsContext.setSupplementChildIds([...supplementsContext.supplementChildIds, supplementChildId]);
    }
  };

  const downloadSepa = async () => {
    try {
      const cod_ramo = localStorage.getItem('policyCodRamo');
      if (cod_ramo) {   
        const args = {
          arrayBuffer: false,
          params: [`isLife=${ cod_ramo === '4' ? true : false}`],
          loader: true,
          setLoading: () => { },
        };
        const response = await SupplementsServices.getSEPATempleate(args);
        const data = Uint8Array.from(response.data.data);
        const blob = new Blob([data.buffer], { type: response.contentType });
        downloadBlob(blob, response.name);
      }
    } catch (error) {
      setDownloadMsg("Ha ocurrido un error durante la descarga del documento.");
      setDownloadError(true);
    }
  }

  const disabledCheckBox = values.nameSelected === "-1";

  return (
    <PersonalInformationContainer>
      {values.type === "MASCOTA"
        ? (
          match && match.params.formTitle === SUPPLEMENTS_MATCH_PARAMS.CAMBIO_DATOS_BASICOS ? <PetForm formData={formData} step={step} /> : null
        ) : (
        <div className="personal-data-fisica">
          {supplementChilds.map((child: SupplementChild) => {
            const { id, title } = child;
            return (
              <div className="personal-data-fisica" key={id}>

                {/* Nombre */}
                {title.includes("Nombre y Apellidos") && (
                  <CheckboxWrapper
                    label={"Modificar Nombre y Apellidos de documento"}
                    isChecked={values.check.name}
                    step="check"
                    onCheckboxChange={(name: string, value: boolean, step?: string) =>
                      handleOnCheck(id, name, value, step)
                    }
                    name="name"
                    disabled={disabledCheckBox}
                  >
                    <div className="personal-data-row">
                      <div className="personal-data-item col-50">
                        <div className="inputBox">
                          <InputBox
                            placeholder="Nombre"
                            required={values.check.name}
                            errorCode={errors.newName}
                            disabled={!values.check.name}
                            value={values.newName}
                            onChange={(value: string) => handleChange("newName", value)}
                            notScroll
                          />
                        </div>
                      </div>
                      <div className="personal-data-item col-50">
                        <div className="inputBox">
                          <InputBox
                            placeholder="Apellido 1"
                            required={values.check.name}
                            errorCode={errors.newLastname1}
                            disabled={!values.check.name}
                            value={values.newLastname1}
                            onChange={(value: string) => handleChange("newLastname1", value)}
                            notScroll
                          />
                        </div>
                      </div>
                    </div>
                    <div className="personal-data-row">
                      <div className="personal-data-item col-50">
                        <div className="inputBox">
                          <InputBox
                            placeholder="Apellido 2"
                            required={
                              values.newNationality &&
                              values.newNationality.toLowerCase() === "españa"
                            }
                            errorCode={errors.newLastname2}
                            disabled={!values.check.name}
                            value={values.newLastname2}
                            onChange={(value: string) => handleChange("newLastname2", value)}
                            notScroll
                          />
                        </div>
                      </div>
                      <div className="personal-data-item col-50"></div>
                    </div>
                  </CheckboxWrapper>
                )}

                {/* Documento */}
                {title.includes("Numero de documento") && (
                  <CheckboxWrapper
                    label={"Modificar Número de documento"}
                    isChecked={values.check.doc}
                    step="check"
                    onCheckboxChange={(name: string, value: boolean, step?: string) =>
                      handleOnCheck(id, name, value, step)
                    }
                    name="doc"
                    disabled={disabledCheckBox}
                  >
                    <div className="personal-data-row">
                      <div className="personal-data-item col-50">
                        <div className="inputBox">
                          <InputBox
                            placeholder="Nº de documento"
                            required={true}
                            errorCode={""}
                            value={values.docNumber}
                            disabled={true}
                            onChange={() => {}}
                            type={"NIF"}
                            notScroll
                          />
                        </div>
                      </div>
                      <div className="personal-data-item col-50"></div>
                    </div>
                    <div className="personal-data-row">
                      <div className="personal-data-item col-50">
                        <div className="selectBox">
                          <SelectBox
                            placeholder="Documento de identidad"
                            required={values.check.doc}
                            errorCode={errors.newDocType}
                            optionsText={getDocumentTypes(values.newNationality, false, true)}
                            optionKey={"value"}
                            optionValue={"label"}
                            initialValue={values.newDocType}
                            defaultValue={values.newDocType}
                            disabled={!values.check.doc}
                            onChange={(value: string) => handleChange("newDocType", value)}
                          />
                          <label>{"Documento de identidad"}</label>
                        </div>
                      </div>
                      <div className="personal-data-item col-50">
                        <div className="inputBox">
                          <InputBox
                            placeholder="Nº de documento"
                            required={values.check.doc}
                            errorCode={errors.newDocNumber}
                            maxLength={DOC_NUMBER_MAX_LENGTH}
                            value={values.newDocNumber}
                            disabled={!values.check.doc || values.newDocType === "MENOR"}
                            onChange={(value: string) =>
                              handleChange("newDocNumber", value.toUpperCase())
                            }
                            type={
                              values.newDocType === "NIF"
                                ? "NIF"
                                : values.newDocType === "NIE"
                                ? "NIE"
                                : values.newDocType === "PAS"
                                ? "PAS"
                                : "NIF"
                            }
                            notScroll
                          />
                        </div>
                      </div>
                    </div>
                  </CheckboxWrapper>
                )}

                {/* Fecha Nacimiento */}
                {title.includes(
                  "Fecha de nacimiento (impacto en prima si se trata de asegurado)"
                ) && (
                  <CheckboxWrapper
                    label={
                      "Modificar Fecha de nacimiento (impacto en prima si se trata de asegurado)"
                    }
                    isChecked={values.check.birthDate}
                    step="check"
                    onCheckboxChange={(name: string, value: boolean, step?: string) =>
                      handleOnCheck(id, name, value, step)
                    }
                    name="birthDate"
                    disabled={disabledCheckBox}
                  >
                    <div className="personal-data-row">
                      <div className="personal-data-item col-50">
                        <div className="inputBox">
                          <CalendarBox
                            initialValue={values.birthDate}
                            placeholder="Fecha de nacimiento actual"
                            disabled={true}
                            onChange={() => {}}
                            notScroll
                          />
                        </div>
                      </div>
                      <div className="personal-data-item col-50">
                        <div className="inputBox">
                          <CalendarBox
                            initialValue={values.newBirthDate}
                            placeholder="Nueva fecha de nacimiento"
                            forbidFutureDates
                            minAge={0}
                            maxDate={moment().toISOString()}
                            required={values.check.birthDate}
                            disabled={!values.check.birthDate}
                            errorCode={errors.newBirthDate}
                            onChange={(value: string) => handleChange("newBirthDate", value)}
                            health={true}
                            notScroll
                          />
                        </div>
                      </div>
                    </div>
                  </CheckboxWrapper>
                )}

                {/* Género */}
                {title.includes("Genero") && (
                  <CheckboxWrapper
                    label={"Modificar Género"}
                    isChecked={values.check.gender}
                    step="check"
                    onCheckboxChange={(name: string, value: boolean, step?: string) =>
                      handleOnCheck(id, name, value, step)
                    }
                    name="gender"
                    disabled={disabledCheckBox}
                  >
                    <div className="personal-data-row">
                      <div className="personal-data-item col-50">
                        <div className="personal-data-item col-25">
                          <DicotomicButton
                            labelText="Sexo"
                            options={genderOptions}
                            withTab={true}
                            disabled={true}
                            initialValue={values.gender}
                            onChange={() => {}}
                            notScroll
                          />
                        </div>
                      </div>
                      <div className="personal-data-item col-50">
                        <div className="personal-data-item col-25">
                          <DicotomicButton
                            labelText="Sexo"
                            options={genderOptions}
                            withTab={true}
                            errorCode={errors.newGender}
                            required={values.check.gender}
                            disabled={!values.check.gender}
                            initialValue={values.newGender}
                            onChange={(value: string) => handleChange("newGender", value)}
                            notScroll
                          />
                        </div>
                      </div>
                    </div>
                  </CheckboxWrapper>
                )}

                {/* Nacionalidad */}
                {title.includes("Nacionalidad") && (
                  <CheckboxWrapper
                    label={"Modificar Nacionalidad"}
                    isChecked={values.check.nationality}
                    step="check"
                    onCheckboxChange={(name: string, value: boolean, step?: string) =>
                      handleOnCheck(id, name, value, step)
                    }
                    name="nationality"
                    disabled={disabledCheckBox}
                  >
                    <div className="personal-data-row">
                      <div className="personal-data-item col-50">
                        <NationalitySelect
                          disabled={!values.check.nationality}
                          required={values.check.nationality}
                          nationalityValue={values.newNationality}
                          nationalityError={errors.newNationality}
                          onSelect={(value, code) => handleChangeNationality(value, code)}
                        />
                      </div>
                      <div className="personal-data-item col-50"></div>
                    </div>
                  </CheckboxWrapper>
                )}

                {/* Forma de pago */}
                {title.includes("Forma de pago") && (
                  <CheckboxWrapper
                    label={"Modificar Forma de pago"}
                    isChecked={values.check.paymentType}
                    step="check"
                    onCheckboxChange={(name: string, value: boolean, step?: string) =>
                      handleOnCheck(id, name, value, step)
                    }
                    name="paymentType"
                    disabled={disabledCheckBox}
                  >
                    <div className="personal-data-row">
                      <div className="personal-data-item col-50">
                        <div className="selectBox">
                          <SelectBox
                            labelText=""
                            required={false}
                            optionsText={filterStringProperties(payType)}
                            initialValue={values.paymentType}
                            defaultValue={values.paymentType}
                            optionKey={"value"}
                            optionValue={"label"}
                            onChange={() => {}}
                            disabled
                          />
                          <label>Forma de pago Actual</label>
                        </div>
                      </div>
                      <div className="personal-data-item col-50">
                        <div className="selectBox">
                          <SelectBox
                            required={true}
                            errorCode={errors.newPaymentType}
                            optionsText={filterStringProperties(payType)}
                            initialValue={values.newPaymentType}
                            defaultValue={values.newPaymentType}
                            optionKey={"value"}
                            optionValue={"label"}
                            onChange={(value: string) => handleChange("newPaymentType", value)}
                          />
                          <label>Nueva Forma de pago</label>
                        </div>
                      </div>
                    </div>
                  </CheckboxWrapper>
                )}

                {/* Cambio domicilio */}
                {title.includes("Cambio domicilio") && (
                  <CheckboxWrapper
                    label={"Modificar Cambio domicilio"}
                    isChecked={values.check.address}
                    step="check"
                    onCheckboxChange={(name: string, value: boolean, step?: string) =>
                      handleOnCheck(id, name, value, step)
                    }
                    name="address"
                    disabled={disabledCheckBox}
                  >
                    <div className="personal-data-row">
                      <div className="personal-data-item col-50">
                        <div className="inputBox">
                          <InputBox
                            placeholder="Provincia Actual"
                            disabled
                            value={values.oldProvince}
                            onChange={() => {}}
                            type={"letters"}
                            notScroll
                          />
                        </div>
                      </div>
                      <div className="personal-data-item col-50">
                        <div className="inputBox">
                          <InputBox
                            placeholder="Población Actual"
                            disabled
                            value={values.oldTown}
                            onChange={() => {}}
                            type={"letters"}
                            notScroll
                          />
                        </div>
                      </div>
                    </div>
                    <div className="personal-data-row">
                      <div className="personal-data-item col-50">
                        <div className="inputBox">
                          <InputBox
                            placeholder="Dirección Actual"
                            required={false}
                            disabled
                            value={values.oldAddress}
                            onChange={() => {}}
                            type={"letters"}
                            notScroll
                          />
                        </div>
                      </div>
                      <div className="personal-data-item col-50"></div>
                    </div>
                    <AddressForm
                      values={values}
                      errors={errors}
                      handleChange={handleChange}
                      hideTitle
                    />
                    <div className="personal-data-row">
                      <div className="personal-data-item col-100">
                        <div className="selectBox margin-left-40 message">
                          <DicotomicCheckbox
                            className={"optional"}
                            checkboxText={
                              "Marque la casilla si el cambio de domicilio debe aplicarse al tomador y todos los asegurados de la póliza."
                            }
                            onChange={(value: boolean) =>
                              handleChange("allInsurancesChange", value, "address")
                            }
                            initChecked={values.address.allInsurancesChange}
                          />
                        </div>
                      </div>
                    </div>
                  </CheckboxWrapper>
                )}

                {/* Teléfono */}
                {title.includes("Teléfono") && (
                  <CheckboxWrapper
                    label={"Modificar teléfono"}
                    isChecked={values.check.phone}
                    step="check"
                    onCheckboxChange={(name: string, value: boolean, step?: string) =>
                      handleOnCheck(id, name, value, step)
                    }
                    name="phone"
                    disabled={disabledCheckBox}
                  >
                    <div className="colSin-100">
                      <div className="colSin-50 rightPadding20 topMargin20 bottomMargin20">
                        <div className="inputBox">
                          <PhoneBox
                            errorCode={deepGet(errors, "phone.mobilePhone", "")}
                            onChange={(value: string) =>
                              handleChange("mobilePhone", value, "phone")
                            }
                            labelText=""
                            required={true}
                            initialValue={values.phone.mobilePhone}
                            disabled={false}
                            notScroll
                          />
                          <label>teléfono móvil</label>
                        </div>
                      </div>
                      <div className="colSin-50 rightPadding20 topMargin20 bottomMargin20">
                        <div className="inputBox">
                          <InputBox
                            errorCode={deepGet(errors, "phone.landlinePhone", "")}
                            onChange={(value: string) =>
                              handleChange("landlinePhone", value, "phone")
                            }
                            placeholder="TELÉFONO FIJO"
                            type={"number"}
                            value={values.phone.landlinePhone}
                            maxLength={12}
                            disabled={false}
                            notScroll
                          />
                        </div>
                      </div>
                    </div>
                  </CheckboxWrapper>
                )}

                {/* Email */}
                {title.includes("Correo electrónico") && (
                  <CheckboxWrapper
                    label={"Modificar Email"}
                    isChecked={values.check.email}
                    step="check"
                    onCheckboxChange={(name: string, value: boolean, step?: string) =>
                      handleOnCheck(id, name, value, step)
                    }
                    name="email"
                    disabled={disabledCheckBox}
                  >
                    <div className="colSin-100 rightPadding20 bottomMargin20">
                      <div className="inputBox">
                        <InputBox
                          errorCode={emailError || deepGet(errors, "email", "")}
                          onChange={(value: string) => {
                            handleChange("email", value);
                            setEmailError && setEmailError("");
                          }}
                          placeholder="CORREO ELECTRÓNICO"
                          required={true}
                          type={"email"}
                          value={values.email}
                          notScroll
                        />
                      </div>
                    </div>
                  </CheckboxWrapper>
                )}

                {/* Actualización IBAN */}
                {title.includes("Actualización IBAN") && (
                  <CheckboxWrapper
                    label={"Modificar IBAN"}
                    isChecked={values.check.iban}
                    step="check"
                    onCheckboxChange={(name: string, value: boolean, step?: string) =>
                      handleOnCheck(id, name, value, step)
                    }
                    name="iban"
                    disabled={disabledCheckBox}
                  >
                    <div className="personal-data-row">
                      <div className="personal-data-item col-50">
                        <div className="inputBox">
                          <InputBox
                            placeholder="IBAN"
                            required
                            maxLength={24}
                            errorCode={errors.iban}
                            value={values.iban}
                            onChange={(value: string) => handleChange("iban", value)}
                            type={"text"}
                            notScroll
                          />
                        </div>
                      </div>
                      <div className="col-50 sepa-button">
                        <MainButton
                          withTab={true}
                          text="Descargar plantilla SEPA"
                          onClick={() => downloadSepa()}
                        />
                      </div>
                    </div>
                  </CheckboxWrapper>
                )}
              </div>
            );
          })}
        </div>
      )}

      <LeftInfoModal
        img={images.IconWarningYellow}
        className={'warning'}
        mainTitle={'Se ha producido un error'}
        mainText={downloadMsg }
        buttonText={'Aceptar'}
        active={downloadError}
        onClickAccept={() => setDownloadError(false)}
        close={() => setDownloadError(false)}
        noSecondaryButton
      />
    </PersonalInformationContainer>
  );
};

export default withRouter(VariableData);
